import { Circle, Group, Layer, Line, Text } from 'react-konva'
import type Konva from 'konva'
import { useEditor, activeLevel } from '@/state/store'
import type { Prop } from '@/model/types'
import { snap, snapPoint } from '@/lib/units'
import { Footprint2D } from '@/components/props/footprints'
import { propDimensions } from '@/components/families/catalog'

const GOLD = '#fbbf24'
const ROTATE_SNAP = 15 // degrees

function findProp(draft: any, id: string): Prop | undefined {
  for (const lvl of draft.levels) {
    const p = lvl.props.find((x: Prop) => x.id === id)
    if (p) return p
  }
  return undefined
}

/** Interactive plan symbols for props: select, drag-to-move (snapped), rotate handle. */
export function PropLayer() {
  const level = useEditor(activeLevel)
  const scale = useEditor((s) => s.view.scale)
  const tool = useEditor((s) => s.tool)

  if (level.props.length === 0) return null

  return (
    <Layer listening={tool === 'select'}>
      {level.props.map((prop) => (
        <PropSymbol key={prop.id} prop={prop} scale={scale} />
      ))}
    </Layer>
  )
}

function PropSymbol({ prop, scale }: { prop: Prop; scale: number }) {
  const update = useEditor((s) => s.update)
  const beginHistory = useEditor((s) => s.beginHistory)
  const select = useEditor((s) => s.select)
  const selection = useEditor((s) => s.selection)
  const snapInc = useEditor((s) => s.project.settings.gridSnap)

  const selected = selection?.kind === 'prop' && selection.id === prop.id
  const px = (n: number) => n / scale

  const dims = propDimensions(prop.familyId, prop.params)
  const w = dims.width
  const d = dims.depth
  const hd = d / 2

  function setPosition(x: number, y: number, transient: boolean) {
    update((draft) => {
      const p = findProp(draft, prop.id)
      if (p) p.position = { x, y }
    }, { transient })
  }
  function setRotation(deg: number, transient: boolean) {
    update((draft) => {
      const p = findProp(draft, prop.id)
      if (p) p.rotation = deg
    }, { transient })
  }

  function onBodyDragMove(e: Konva.KonvaEventObject<DragEvent>) {
    const snapped = snapPoint({ x: e.target.x(), y: e.target.y() }, snapInc)
    e.target.position(snapped)
    setPosition(snapped.x, snapped.y, true)
  }

  const handleOffset = hd + px(18)

  function onRotateDragMove(e: Konva.KonvaEventObject<DragEvent>) {
    const stage = e.target.getStage()
    const ptr = stage?.getRelativePointerPosition()
    if (!ptr) return
    const deg = (Math.atan2(ptr.y - prop.position.y, ptr.x - prop.position.x) * 180) / Math.PI + 90
    const norm = ((snap(deg, ROTATE_SNAP) % 360) + 360) % 360
    setRotation(norm, true)
  }

  return (
    <Group
      x={prop.position.x}
      y={prop.position.y}
      rotation={prop.rotation}
      draggable
      onMouseDown={(e) => {
        e.cancelBubble = true
        select({ kind: 'prop', id: prop.id })
      }}
      onDragStart={() => beginHistory()}
      onDragMove={onBodyDragMove}
    >
      <Footprint2D prop={prop} scale={scale} />

      {selected && (
        <Line
          points={[-w / 2, -hd, w / 2, -hd, w / 2, hd, -w / 2, hd]}
          closed
          stroke={GOLD}
          strokeWidth={px(1.6)}
          dash={[px(4), px(3)]}
          listening={false}
        />
      )}

      <Text
        text={prop.label ?? ''}
        x={0}
        y={hd + px(4)}
        offsetX={6}
        width={12}
        rotation={-prop.rotation}
        fontSize={1}
        fill="#cbd3e4"
        align="center"
        listening={false}
      />

      {selected && (
        <>
          <Line points={[0, -hd, 0, -handleOffset]} stroke={GOLD} strokeWidth={px(1.2)} listening={false} />
          <Circle
            x={0}
            y={-handleOffset}
            radius={px(5)}
            fill={GOLD}
            stroke="#1c2230"
            strokeWidth={px(1)}
            draggable
            onMouseDown={(e) => { e.cancelBubble = true }}
            onDragStart={(e) => { e.cancelBubble = true; beginHistory() }}
            onDragMove={onRotateDragMove}
          />
        </>
      )}
    </Group>
  )
}
